import { InputAdornment } from '@mui/material';
import type { InputBaseComponentProps } from '@mui/material/InputBase';
import { type ElementType, type ReactNode } from 'react';
import type { NumberFormatValues } from 'react-number-format';
import {
  FormTextField,
  type FormTextFieldProps,
} from 'src/components/Forms/FormTextField/form-text-field';
import {
  NumericFormatInput,
  type NumericFormatInputProps,
} from 'src/components/Forms/NumericFormatInput/numeric-format-input';

export type FormPriceValueType = 'number' | 'string';

export type FormPriceFieldProps = Omit<
  FormTextFieldProps,
  'type' | 'textFieldProps' | 'value' | 'pattern' | 'underlined' | 'onChangeDebounceDelay'
> & {
  /** `number` stores the parsed amount; `string` stores the digits as typed, e.g. `"1250.5"`. */
  valueAs?: FormPriceValueType;
  /** What a cleared field stores when `valueAs` is `number`. */
  emptyValue?: number | '';
  /** Exclusive upper bound: a keystroke that would reach it is refused. */
  max?: number;
  endAdornment?: ReactNode;
  placeholder?: string;
  inputTestId?: string;
};

type PriceInputProps = Pick<
  NumericFormatInputProps,
  'parseValue' | 'ignoreProgrammaticChanges' | 'isAllowed'
> & { 'data-testid'?: string };

// InputBase types a custom input's onChange as a DOM handler, but react-hook-form only reads `target`
const priceInputComponent =
  NumericFormatInput as unknown as ElementType<InputBaseComponentProps>;

const dollarAdornment = <InputAdornment position="start">$</InputAdornment>;

export const FormPriceField = ({
  valueAs = 'number',
  emptyValue = '',
  max,
  endAdornment,
  placeholder,
  inputTestId,
  ...rest
}: FormPriceFieldProps) => {
  const priceInputProps: PriceInputProps = {
    ...(valueAs === 'number' && {
      parseValue: ({ floatValue }: NumberFormatValues) => floatValue ?? emptyValue,
      // Re-formatting a number the form wrote reports it back as a change; only typing should.
      ignoreProgrammaticChanges: true,
    }),
    ...(max !== undefined && {
      isAllowed: ({ floatValue = 0 }: NumberFormatValues) => floatValue < max,
    }),
    'data-testid': inputTestId,
  };

  return (
    <FormTextField
      {...rest}
      type="textfield"
      value=""
      textFieldProps={{
        placeholder,
        InputProps: {
          inputComponent: priceInputComponent,
          startAdornment: dollarAdornment,
          ...(endAdornment
            ? { endAdornment: <InputAdornment position="end">{endAdornment}</InputAdornment> }
            : {}),
        },
        inputProps: priceInputProps,
      }}
    />
  );
};
